import {
	Avatar,
	Button,
	Card,
	CardContent,
	// FormControl,
	Grid,
	TextField,
	Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import React, { useEffect } from 'react';
import { usePermissionCheck } from '../../../core/hooks/usePermissionCheck';
import { useAuth } from '../../auth/context/AuthContext';
import { getImageUrl, userProfilesService } from '../../../core/API/userProfiles';
import { apiService } from '../../../core/services/apiService';
import BasicDatePicker from '../../../core/components/DatePicker';
import dayjs from 'dayjs';
// import Autocomplete from '@mui/material/Autocomplete';
// import { GetTimeZones } from '../../../core/API/workplaces';

export const ProfileForm = () => {
	const { user } = useAuth();
	const { hasEditPermission } = usePermissionCheck();
	const canEditProfile = hasEditPermission('Profile');

	const [isLoading, setIsLoading] = React.useState(true);
	// const [timeZones, setTimeZones] = React.useState([]);

	// useEffect(() => {
	// 	const fetchTimeZones = async () => {
	// 		try {
	// 			const zones = await GetTimeZones();
	// 			setTimeZones(zones);
	// 		} catch (error) {
	// 			console.error('Failed to fetch timezones:', error);
	// 		}
	// 	};
	// 	fetchTimeZones();
	// }, []);

	const formik = useFormik({
		initialValues: {
			profilePictureName: '',
			nickName: '',
			fullName: `${user?.firstName} ${user?.lastName}`,
			// birthday: '',
			// timezone: '',
		},
		validationSchema: Yup.object({
			nickName: Yup.string().max(50, 'Nick name must be 50 characters or less'),
		}),
		onSubmit: async (values) => {
			try {
				setIsLoading(true);
				await userProfilesService.updateUserProfile({
					profilePictureFile: null,
					nickname: values.nickName,
					// dateOfBirth: values.birthday,
					// timezone: values.timezone,
				});
			} catch (error) {
				console.error('Failed to update profile:', error);
			} finally {
				setIsLoading(false);
			}
		},
	});

	useEffect(() => {
		const fetchProfile = async () => {
			try {
				setIsLoading(true);

				const profile = await userProfilesService.getUserProfile(user?.id);

				formik.resetForm({
					values: {
						...formik.values,
						nickName: profile.nickname || '',
						// birthday: profile.dateOfBirth || '',
						// timezone: profile.timezone || '',
						profilePictureName: profile.profilePictureName || '',
						fullName: `${user?.firstName} ${user?.lastName}`,
					}
				});
			} catch (error) {
				console.error('Failed to fetch profile:', error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchProfile();
	}, [user]);
	const inputRef = React.useRef<HTMLInputElement | null>(null);

	const isDirty = formik.dirty;

	const handleChange = formik.handleChange;
	const profile = formik.values;

	return (
		<Card sx={{ height: '100%' }}>
			<CardContent>
				<Grid container justifyContent={'space-between'} alignItems="center">
					<Grid direction="row" alignItems="center">
						<Typography variant="h5" sx={{ fontFamily: 'Gotham' }}>Profile - {user?.email}</Typography>
					</Grid>
					<Grid direction="row-reverse" justifyContent={'flex-end'}>
						<Button
							variant="contained"
							disabled={!isDirty || isLoading || !canEditProfile}
							sx={{ backgroundColor: '#446388', marginTop: '3%' }}
							onClick={() => formik.handleSubmit()}
						>
							{isLoading ? 'Saving...' : 'Save'}
						</Button>
					</Grid>
				</Grid>
				<Grid container direction="column" spacing={2} sx={{ margin: '2%' }}>
					<Grid
						item
						sx={{
							position: 'relative',
							textAlign: 'center',
							margin: '2% 0',
							width: 80,
							height: 80,
							paddingTop: 0,
						}}
					>
						<Avatar
							src={profile.profilePictureName ? getImageUrl(profile.profilePictureName) : ''}
							alt="Profile Picture"
							sx={{
								position: 'absolute',
								top: 0,
								left: 0,
								width: 80,
								height: 80,
								cursor: canEditProfile ? 'pointer' : 'not-allowed',
							opacity: canEditProfile ? 1 : 0.6,
							}}
							onClick={() => canEditProfile && inputRef.current?.click()}
						/>

						<input
							accept="image/*"
							type="file"
							ref={inputRef}
							style={{ display: 'none' }}
							onChange={async (e) => {
								const file = e.target.files?.[0];
								// Prevent upload if nickname exceeds 50 characters
								if (profile.nickName && profile.nickName.length > 50) {
									alert('Nick name must be 50 characters or less. Please shorten it before uploading a picture.');
									e.target.value = '';
									return;
								}
								const MAX_SIZE_MB = 5;
								if (file) {
									if (file.size > MAX_SIZE_MB * 1024 * 1024) {
										alert(`File size should not exceed ${MAX_SIZE_MB}MB.`);
										e.target.value = '';
										return;
									}
									try {
										setIsLoading(true);
										const formData = new FormData();
										formData.append('profilePictureFile', file);
										formData.append('nickname', profile.nickName);
										// formData.append('dateOfBirth', profile.birthday);
										// formData.append('timezone', profile.timezone);
										const updatedProfile = await apiService.post(
											'user_profiles/create_update/media',
											formData,
											{
												headers: {
													'Content-Type': 'multipart/form-data',
												},
											}
										);
										console.log('Profile picture upload response:', updatedProfile);

										formik.setValues({
											...formik.values,
											...updatedProfile?.data,
										});
									} catch (error) {
										console.error('Failed to upload profile picture:', error);
										alert('Failed to upload profile picture. Please try again.');
									} finally {
										setIsLoading(false);
									}
								}
							}}
						/>
						{/* <IconButton
							sx={{
								position: 'absolute',
								bottom: 0,
								left: 0,
								width: '100%',
								height: '100%',
								backgroundColor: 'rgba(0,0,0,0.2)',
								color: '#446388',
								zIndex: 1,
								transition: 'opacity 0.3s',
							}}
							className="profile-avatar-hover-icon"
						>
							<CameraAltIcon />
						</IconButton> */}
					</Grid>
				</Grid>

				<TextField
					fullWidth
					label="Nick Name"
					name="nickName"
					value={profile.nickName}
					onChange={handleChange}
					onBlur={formik.handleBlur}
					error={formik.touched.nickName && Boolean(formik.errors.nickName)}
					helperText={formik.touched.nickName && (formik.errors.nickName as string)}
					disabled={!canEditProfile}
					margin="normal"
					sx={{ fontFamily: 'Gotham' }}
				/>
				<TextField
					fullWidth
					label="Full Name"
					name="fullName"
					disabled
					value={profile.fullName}
					onChange={handleChange}
					margin="normal"
					sx={{ fontFamily: 'Gotham' }}
				/>
				{/* <BasicDatePicker
					label="Birthday"
					name="birthday"
					value={profile.birthday ? dayjs(profile.birthday) : null}
					onChange={(date) => formik.setFieldValue('birthday', date?.format('YYYY-MM-DD'))}
					disabled={!canEditProfile}
					margin="normal"
					sx={{ fontFamily: 'Gotham' }}
				/> */}

				{/* <FormControl fullWidth margin="normal" sx={{ fontFamily: 'Gotham' }}>
					<Autocomplete
						options={timeZones}
						getOptionLabel={(option) => option.label}
						value={timeZones.find((tz) => tz['@id'] === profile.timezone) || null}
						onChange={(_, value) => formik.setFieldValue('timezone', value?.['@id'])}
						disabled={!canEditProfile}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Timezone"
								error={formik.touched.timezone && Boolean(formik.errors.timezone)}
								helperText={formik.touched.timezone && (formik.errors.timezone as string)}
								sx={{ fontFamily: 'Gotham' }}
							/>
						)}
					/>
				</FormControl> */}
			</CardContent>
		</Card>
	);
};
